
import React, { useState } from 'react';
import type { Language, WeekStartDay } from '../types';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { getTranslation, isRTL } from '../utils/translations';

interface CalendarPickerProps {
    isOpen: boolean;
    onClose: () => void;
    selectedDate: Date;
    onSelectDate: (date: Date) => void;
    language?: Language;
    weekStartDay?: WeekStartDay;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({ 
    isOpen, 
    onClose, 
    selectedDate, 
    onSelectDate, 
    language = 'en', 
    weekStartDay = 'Monday' 
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [viewDate, setViewDate] = useState(new Date(selectedDate));
    const rtl = isRTL(language);

    if (!isOpen) return null;

    const currentMonth = viewDate.getMonth();
    const currentYear = viewDate.getFullYear();

    const handlePrevMonth = () => {
        const d = new Date(viewDate);
        d.setMonth(d.getMonth() - 1);
        setViewDate(d);
    };

    const handleNextMonth = () => {
        const d = new Date(viewDate);
        d.setMonth(d.getMonth() + 1);
        setViewDate(d);
    };

    // Calendar Grid Logic
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay(); // 0 (Sun) - 6 (Sat)

    // Adjust for week start
    const startOffset = weekStartDay === 'Sunday' ? 0 : 1;
    const adjustedFirstDay = (firstDayOfMonth - startOffset + 7) % 7;

    const days = [];
    // Padding days
    for (let i = 0; i < adjustedFirstDay; i++) {
        days.push(null);
    }
    // Actual days
    for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(currentYear, currentMonth, i));
    }

    // Header Labels (S M T W T F S)
    const weekDays = weekStartDay === 'Sunday' 
        ? [0, 1, 2, 3, 4, 5, 6] 
        : [1, 2, 3, 4, 5, 6, 0];
    
    const weekDayLabels = weekDays.map(d => {
        const date = new Date(2023, 0, 1 + d); // Jan 1 2023 was Sunday
        return date.toLocaleDateString(language, { weekday: 'narrow' }); // 'S', 'M', etc.
    });

    const monthLabel = viewDate.toLocaleDateString(language, { month: 'long', year: 'numeric' });

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.getDate() === d2.getDate() && 
               d1.getMonth() === d2.getMonth() && 
               d1.getFullYear() === d2.getFullYear();
    };

    const today = new Date();

    const prevIconClass = rtl ? 'w-5 h-5 -rotate-90' : 'w-5 h-5 rotate-90';
    const nextIconClass = rtl ? 'w-5 h-5 rotate-90' : 'w-5 h-5 -rotate-90';

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div
                dir={rtl ? 'rtl' : 'ltr'}
                className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl w-full max-w-sm overflow-hidden border border-white/20 dark:border-slate-800"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800">
                    <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 capitalize tracking-tight">{monthLabel}</h3>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={handlePrevMonth}
                            className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-white dark:bg-slate-900 rounded-xl shadow-sm transition-all border border-slate-100 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                            aria-label={t('previousMonth')}
                        >
                            <ChevronDownIcon className={prevIconClass} />
                        </button>
                        <button
                            type="button"
                            onClick={handleNextMonth}
                            className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-white dark:bg-slate-900 rounded-xl shadow-sm transition-all border border-slate-100 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                            aria-label={t('nextMonth')}
                        >
                            <ChevronDownIcon className={nextIconClass} />
                        </button>
                    </div>
                </div>

                {/* Days Header */}
                <div className="grid grid-cols-7 gap-1 px-4 py-3 bg-white dark:bg-slate-900">
                    {weekDayLabels.map((label, idx) => (
                        <div key={idx} className="text-center text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            {label}
                        </div>
                    ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-2 px-4 pb-6 bg-white dark:bg-slate-900">
                    {days.map((date, idx) => {
                        if (!date) return <div key={idx} />;
                        
                        const isSelected = isSameDay(date, selectedDate);
                        const isToday = isSameDay(date, today);

                        return (
                            <button
                                key={idx}
                                onClick={() => { onSelectDate(date); onClose(); }}
                                className={`
                                    h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all relative
                                    ${isSelected 
                                        ? 'bg-slate-900 text-white shadow-lg scale-105' 
                                        : isToday 
                                            ? 'bg-brand-sage/10 text-brand-sage border border-brand-sage/20' 
                                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }
                                `}
                            >
                                {date.getDate()}
                                {isToday && !isSelected && <div className="absolute bottom-1 w-1 h-1 rounded-full bg-brand-sage"></div>}
                            </button>
                        );
                    })}
                </div>

                <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex justify-center">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors py-2"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CalendarPicker;
